import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import {
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { EService } from '../../../shared/enums/EService';
import { formatDate } from '../../../shared/functions/formatDate';
import { formatNumber } from '../../../shared/functions/formatNumber';
import { getServiceColor } from '../../../shared/functions/getServiceColor';
import { handleError } from '../../../shared/functions/handleError';
import {
  ITruck,
  ITruckBrand,
  ITruckService,
} from '../../../shared/interfaces/ITruck';
import { RootState } from '../../../store/store';
import { AdminTruckBrandService } from '../../truck-brand/service';

interface IProps {
  register: UseFormRegister<ITruck>;
  errors: FieldErrors<ITruck>;
  setValue: UseFormSetValue<ITruck>;
  watch: UseFormWatch<ITruck>;
}
const brandKeys = [
  'firstServiceKm',
  'minorServiceIntervalKm',
  'majorServiceIntervalKm',
];

const TruckForm = ({ errors, register, watch, setValue }: IProps) => {
  const { t } = useTranslation();
  const tableRef = useRef<HTMLDivElement | null>(null);
  const { services } = useSelector((state: RootState) => state.enums);
  const [truckBrands, setTruckBrands] = useState<ITruckBrand[]>([]);
  const selectedTruckBrand = truckBrands.find(
    (brand) => brand.id === Number(watch('brandId')),
  );

  const handleAddService = (service: EService) => {
    tableRef?.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    setValue('services', [
      {
        mileage: watch('mileage'),
        type: service,
      } as ITruckService,
      ...(watch('services') ?? []),
    ]);
  };

  useEffect(() => {
    AdminTruckBrandService.getAll({ page: 1, perPage: 0 })
      .then((response) => {
        setTruckBrands(response?.data);
      })
      .catch(handleError);
  }, []);
  return (
    <Row>
      <Col className={'pe-0'} md={6}>
        <Row>
          <Col md={12}>
            <h5 className={'mb-3'}>{t('general.details')}</h5>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3" controlId="form.licensePlate">
              <Form.Label>{t('general.licensePlate')}</Form.Label>
              <Form.Control
                type="text"
                placeholder={t('general.licensePlate')}
                isInvalid={!!errors.licensePlate}
                {...register('licensePlate')}
              />
              {errors?.licensePlate && (
                <Form.Control.Feedback type="invalid">
                  {errors.licensePlate.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3" controlId="form.mileage">
              <Form.Label>{t('general.mileage')}</Form.Label>
              <Form.Control
                type="number"
                placeholder={t('general.mileage')}
                isInvalid={!!errors.mileage}
                {...register('mileage')}
              />
              {errors?.mileage && (
                <Form.Control.Feedback type="invalid">
                  {errors.mileage.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3" controlId="form.nextServiceKm">
              <Form.Label>{t('general.nextServiceKm')}</Form.Label>
              <Form.Control
                type="number"
                placeholder={t('general.nextServiceKm')}
                isInvalid={!!errors.nextServiceKm}
                {...register('nextServiceKm')}
              />
              {errors?.nextServiceKm && (
                <Form.Control.Feedback type="invalid">
                  {errors.nextServiceKm.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3" controlId="form.alarmSystem">
              <Form.Label>{t('general.alarmSystem')}</Form.Label>
              <Form.Control
                type="date"
                placeholder={t('general.alarmSystem')}
                isInvalid={!!errors.alarmSystem}
                {...register('alarmSystem')}
              />
              {errors?.alarmSystem && (
                <Form.Control.Feedback type="invalid">
                  {errors.alarmSystem.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3" controlId="form.tachograph">
              <Form.Label>{t('general.tachograph')}</Form.Label>
              <Form.Control
                type="date"
                placeholder={t('general.tachograph')}
                isInvalid={!!errors.tachograph}
                {...register('tachograph')}
              />
              {errors?.tachograph && (
                <Form.Control.Feedback type="invalid">
                  {errors.tachograph.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3" controlId="form.technicalInspection">
              <Form.Label>{t('general.technicalInspection')}</Form.Label>
              <Form.Control
                type="date"
                placeholder={t('general.technicalInspection')}
                isInvalid={!!errors.technicalInspection}
                {...register('technicalInspection')}
              />
              {errors?.technicalInspection && (
                <Form.Control.Feedback type="invalid">
                  {errors.technicalInspection.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>

          {truckBrands?.length && (
            <Col md={6}>
              <Form.Group className="mb-3" controlId="form.brandId">
                <Form.Label>{t('general.brand')}</Form.Label>
                <Form.Control
                  as="select"
                  isInvalid={!!errors.brandId}
                  {...register('brandId')}
                >
                  <option value="">{t('general.selectBrand')}</option>
                  {truckBrands?.map((truckBrand) => {
                    return (
                      <option value={truckBrand?.id} key={truckBrand?.id}>
                        {truckBrand?.name}
                      </option>
                    );
                  })}
                </Form.Control>
                {errors?.brandId && (
                  <Form.Control.Feedback type="invalid">
                    {errors.brandId.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          )}
          {selectedTruckBrand?.id && (
            <Col md={12}>
              {brandKeys?.map((brandKey) => {
                return (
                  <h6 key={brandKey} className="m-1">
                    {t(`general.${brandKey}`)}:{' '}
                    {formatNumber(
                      selectedTruckBrand?.[
                        brandKey as keyof typeof selectedTruckBrand
                      ] as number,
                    )}
                  </h6>
                );
              })}
            </Col>
          )}
        </Row>
      </Col>
      <Col md={6} className={'border-start'}>
        <Row>
          <Col md={12}>
            <h5 className={'mb-3'}>{t('general.services')}</h5>
          </Col>
          <Col
            md={12}
            className={'overflow-auto'}
            ref={tableRef}
            style={{ maxHeight: 300 }}
          >
            <table className="table ">
              <thead className="table-dark position-sticky top-0 z-1">
                <tr>
                  <th scope="col" className={'vertical-align-middle'}>
                    #
                  </th>
                  <th scope="col" className={'vertical-align-middle'}>
                    {t('general.type')}
                  </th>
                  <th scope="col" className={'vertical-align-middle'}>
                    {t('general.performedBy')}
                  </th>
                  <th scope="col" className={'vertical-align-middle'}>
                    {t('general.mileage')}
                  </th>
                  <th scope="col" className={'vertical-align-middle'}>
                    {t('general.updatedAt')}
                  </th>
                </tr>
              </thead>
              <tbody className={'border'}>
                {watch('services')?.map((service) => {
                  const isLocalService = !service?.id;
                  return (
                    <tr key={service?.id}>
                      <td className={isLocalService ? 'bg-warning-subtle' : ''}>
                        {service?.id}
                      </td>
                      <td className={isLocalService ? 'bg-warning-subtle' : ''}>
                        {t(`service_type.${service?.type}`)}
                      </td>
                      <td className={isLocalService ? 'bg-warning-subtle' : ''}>
                        {service?.performedBy?.displayName}
                      </td>
                      <td className={isLocalService ? 'bg-warning-subtle' : ''}>
                        {formatNumber(service?.mileage as number)}
                      </td>
                      <td className={isLocalService ? 'bg-warning-subtle' : ''}>
                        {formatDate(service?.performedBy?.updatedAt as string)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Col>
          <Col md={12}>
            <div className="d-flex align-items-center flex-wrap border-top pt-2">
              {services?.map((service) => {
                return (
                  <div key={service} className={'m-1'}>
                    <Button
                      variant={getServiceColor(service)}
                      onClick={() => handleAddService(service)}
                    >
                      {t(`service_type.make.${service}`)}
                    </Button>
                  </div>
                );
              })}
            </div>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default TruckForm;
